/**
 * SRS wall-kick tables, implemented as data.
 *
 * The two tables below are written exactly as they are published, in the
 * convention the published tables use: **y is positive upward**. That is also
 * how the reference prototype stores them, and keeping the published form means
 * a reviewer can diff these literals against any SRS reference without doing
 * arithmetic in their head. They are `prettier-ignore`d so the rows stay one
 * transition per line.
 *
 * The engine's board has y growing downward, so `kicksFor` returns the same
 * offsets with `y` negated. The negation happens in exactly one place.
 *
 * A rotation tries each offset in order and takes the first that does not
 * collide. Offset 0 is always `(0, 0)`, so an unobstructed rotation never moves.
 */
import type { PieceKind, RotationState, Vec2 } from './types.js';

/** One entry of a published kick table. */
export interface KickTransition {
  readonly from: RotationState;
  readonly to: RotationState;
  /** Offsets in published convention: `[dx, dy]` with `dy` positive upward. */
  readonly offsets: readonly Vec2[];
}

/** The published JLSTZ table. Used by J, L, S, T and Z. */
// prettier-ignore
export const SRS_KICKS_JLSTZ_YUP: readonly KickTransition[] = [
  { from: 0, to: 1, offsets: [[0, 0], [-1, 0], [-1,  1], [0, -2], [-1, -2]] },
  { from: 1, to: 0, offsets: [[0, 0], [ 1, 0], [ 1, -1], [0,  2], [ 1,  2]] },
  { from: 1, to: 2, offsets: [[0, 0], [ 1, 0], [ 1, -1], [0,  2], [ 1,  2]] },
  { from: 2, to: 1, offsets: [[0, 0], [-1, 0], [-1,  1], [0, -2], [-1, -2]] },
  { from: 2, to: 3, offsets: [[0, 0], [ 1, 0], [ 1,  1], [0, -2], [ 1, -2]] },
  { from: 3, to: 2, offsets: [[0, 0], [-1, 0], [-1, -1], [0,  2], [-1,  2]] },
  { from: 3, to: 0, offsets: [[0, 0], [-1, 0], [-1, -1], [0,  2], [-1,  2]] },
  { from: 0, to: 3, offsets: [[0, 0], [ 1, 0], [ 1,  1], [0, -2], [ 1, -2]] },
];

/** The published I table. I kicks differently from everything else. */
// prettier-ignore
export const SRS_KICKS_I_YUP: readonly KickTransition[] = [
  { from: 0, to: 1, offsets: [[0, 0], [-2, 0], [ 1, 0], [-2, -1], [ 1,  2]] },
  { from: 1, to: 0, offsets: [[0, 0], [ 2, 0], [-1, 0], [ 2,  1], [-1, -2]] },
  { from: 1, to: 2, offsets: [[0, 0], [-1, 0], [ 2, 0], [-1,  2], [ 2, -1]] },
  { from: 2, to: 1, offsets: [[0, 0], [ 1, 0], [-2, 0], [ 1, -2], [-2,  1]] },
  { from: 2, to: 3, offsets: [[0, 0], [ 2, 0], [-1, 0], [ 2,  1], [-1, -2]] },
  { from: 3, to: 2, offsets: [[0, 0], [-2, 0], [ 1, 0], [-2, -1], [ 1,  2]] },
  { from: 3, to: 0, offsets: [[0, 0], [ 1, 0], [-2, 0], [ 1, -2], [-2,  1]] },
  { from: 0, to: 3, offsets: [[0, 0], [-1, 0], [ 2, 0], [-1,  2], [ 2, -1]] },
];

/** The only offset for a transition the tables do not describe: do not move. */
export const NO_KICKS: readonly Vec2[] = [[0, 0]];

type KickRow = readonly [readonly Vec2[], readonly Vec2[], readonly Vec2[], readonly Vec2[]];
type KickGrid = readonly [KickRow, KickRow, KickRow, KickRow];

/** Look one transition up in a published table and flip it to board coordinates. */
function boardOffsets(
  published: readonly KickTransition[],
  from: RotationState,
  to: RotationState,
): readonly Vec2[] {
  const match = published.find((entry) => entry.from === from && entry.to === to);
  if (match === undefined) return NO_KICKS;
  // `0 - dy` rather than `-dy`: negating a zero would leave `-0` in the data.
  return match.offsets.map(([dx, dy]): Vec2 => [dx, 0 - dy]);
}

function kickRow(published: readonly KickTransition[], from: RotationState): KickRow {
  return [
    boardOffsets(published, from, 0),
    boardOffsets(published, from, 1),
    boardOffsets(published, from, 2),
    boardOffsets(published, from, 3),
  ];
}

function toBoardCoordinates(published: readonly KickTransition[]): KickGrid {
  return [
    kickRow(published, 0),
    kickRow(published, 1),
    kickRow(published, 2),
    kickRow(published, 3),
  ];
}

/** JLSTZ kicks in board coordinates, indexed `[from][to]`. */
export const KICKS_JLSTZ: KickGrid = toBoardCoordinates(SRS_KICKS_JLSTZ_YUP);

/** I kicks in board coordinates, indexed `[from][to]`. */
export const KICKS_I: KickGrid = toBoardCoordinates(SRS_KICKS_I_YUP);

/**
 * Kick candidates for one rotation, in board coordinates and in try order.
 *
 * O has no kicks because O has no rotation: all four of its states are the same
 * cells, so the identity offset always succeeds.
 */
export function kicksFor(kind: PieceKind, from: RotationState, to: RotationState): readonly Vec2[] {
  if (kind === 'O') return NO_KICKS;
  const grid = kind === 'I' ? KICKS_I : KICKS_JLSTZ;
  return grid[from][to];
}
